import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "@outpave/ds";
import {
  QUERY_USER_NOTIFICATION_SETTINGS,
  UPDATE_USER_NOTIFICATION_SETTINGS,
} from "../../../graphql/userNotificationsSettings";
import NotificationChannelTable, {
  NotificationChannelRow,
  NotificationSettingInput,
} from "./NotificationChannelTable";

interface UserNotificationSettingItemProps {
  /** The viewer's role, as the heading reads it. */
  userRole: string;
}

/**
 * A row as the schema actually declares it: only `id` is non-null and the list
 * permits null elements. No `userRole` either, since the query does not select
 * one and the role in the header comes from props.
 */
interface UserNotificationSettingRow {
  id: string;
  category?: string | null;
  isEnable?: boolean | null;
  deliveryModes?: (string | null)[] | null;
}

/** The same row once normalized, which is all the render path deals with. */
interface UserNotificationSetting {
  id: string;
  category: string;
  isEnable: boolean;
  deliveryModes: string[];
}

/**
 * The switch is controlled, so a null `isEnable` would flip it to uncontrolled
 * and React would warn. Normalizing once here keeps every consumer simple.
 */
const normalize = (
  row: UserNotificationSettingRow,
): UserNotificationSetting => ({
  id: row.id,
  category: row.category ?? "",
  isEnable: row.isEnable ?? false,
  deliveryModes: (row.deliveryModes ?? []).filter(
    (mode): mode is string => mode != null,
  ),
});

const UserNotificationSettingsTable: React.FC<
  UserNotificationSettingItemProps
> = (props) => {
  const { userRole } = props;
  const { t } = useTranslation();

  const {
    data: userNotificationData,
    loading: isLoadingSetting,
    refetch: reloadData,
  } = useQuery(QUERY_USER_NOTIFICATION_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const [updateNotificationSetting] = useMutation(
    UPDATE_USER_NOTIFICATION_SETTINGS,
  );

  // Null elements are allowed by the schema, so they go before rendering.
  const rows: NotificationChannelRow[] = useMemo(
    () =>
      (
        (userNotificationData?.myNotificationSettings ??
          []) as (UserNotificationSettingRow | null)[]
      )
        .filter((row): row is UserNotificationSettingRow => row != null)
        .map(normalize)
        .map((setting) => ({
          ...setting,
          title: t(`userNotificationSettings.${setting.category}_title`),
          description: t(
            `userNotificationSettings.${setting.category}_description`,
          ),
        })),
    [userNotificationData, t],
  );

  const saveSetting = async (id: string, input: NotificationSettingInput) => {
    try {
      await updateNotificationSetting({ variables: { id, input } });
      toast.dismiss();
      toast.success(t("userNotificationSettings.updateNotificationSuccess"));
      reloadData();
    } catch (error) {
      toast.error((error as Error)?.message || t("Please try again!"));
    }
  };

  return (
    <NotificationChannelTable
      heading={`${t("Your role")} : ${userRole}`}
      channelLabels={{
        email: t("userNotificationSettings.email"),
        sms: t("userNotificationSettings.sms"),
        push: t("userNotificationSettings.mobilePush"),
      }}
      rows={rows}
      loading={isLoadingSetting && !userNotificationData}
      onSave={saveSetting}
    />
  );
};

export default UserNotificationSettingsTable;
